// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { useState } from "react";
import { Box, Column, Row, Select, Text, Tooltip } from "@hightouch/htui";

import {
  HeatmapTable,
  type CellValueFormatter,
  type HeatmapCellData,
  type HeatmapRow,
  type RowSortFn,
  type SeriesContext,
  type TooltipRenderer,
} from "src/pages/analytics/heatmap-table";
import type { SortFn } from "src/pages/analytics/heatmap-table-utils";
import { GraphScale } from "src/components/graphs/cross-audience-graph/constants";
import { Link } from "src/router";
import { ChannelIcon } from "src/pages/decision-engines/components/channel-icon";
import { ChannelOptionsMap } from "src/pages/decision-engines/utils";
import { TextWithTooltip } from "src/components/text-with-tooltip";

import type {
  GridChartResult,
  ChartConfig,
  LabelResolver,
  SendInsightDimension,
  SendInsightLabel,
  DisplayFormat,
  MetricNumerator,
  MetricDenominator,
} from "./types";
import { DIMENSION_LABELS, getDisplayFormat } from "./types";
import {
  DAY_OF_WEEK_ORDER,
  TIME_BUCKET_LABELS,
  TIME_BUCKET_ORDER,
} from "@hightouch/lib/customer-data/decision-engine/send-insights/constants";

function metricLabel(
  field: MetricNumerator | MetricDenominator,
): string | undefined {
  if (field.startsWith("metadata.")) return field.slice("metadata.".length);
  switch (field) {
    case "matched":
      return "Matched";
    case "sends":
      return "Sends";
    case "recs":
      return "Recommendations";
    case "rolling_30d_users":
      return "30-day active users";
    case "none":
      return "";
  }
  return;
}

function formatValue(value: number | null, format: DisplayFormat): string {
  if (value == null) return "–";
  switch (format) {
    case "percentage":
      return `${(value * 100).toFixed(1)}%`;
    case "ratio":
      return value.toFixed(2);
    case "absolute":
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}

function formatPercent(pct: number | null): string {
  if (pct == null) return "–";
  return `${(pct * 100).toFixed(1)}%`;
}

type PercentMode =
  | { type: "by_experiment"; experimentKey: string }
  | { type: "global" };

function getPercentMode(config: ChartConfig): PercentMode | null {
  const displayFormat = getDisplayFormat(config.denominator);
  if (displayFormat === "percentage") return null;

  if (config.segment_by === "experiment") {
    return { type: "by_experiment", experimentKey: "experiment" };
  }
  return { type: "global" };
}

function computeCellPercentages(
  cells: GridChartResult["cells"],
  mode: PercentMode,
): Map<string, number | null> {
  const pctMap = new Map<string, number | null>();

  const cellKey = (c: GridChartResult["cells"][0]) =>
    Object.entries(c.segments)
      .map(([k, v]) => `${k}=${v}`)
      .join(":");

  if (mode.type === "global") {
    const total = cells.reduce(
      (sum, c) => sum + (c.value != null ? c.value : 0),
      0,
    );
    for (const cell of cells) {
      pctMap.set(
        cellKey(cell),
        cell.value != null && total > 0 ? cell.value / total : null,
      );
    }
  } else {
    const groupTotals = new Map<string, number>();
    for (const cell of cells) {
      const groupKey = String(cell.segments[mode.experimentKey] ?? "");
      groupTotals.set(
        groupKey,
        (groupTotals.get(groupKey) ?? 0) + (cell.value ?? 0),
      );
    }
    for (const cell of cells) {
      const groupKey = String(cell.segments[mode.experimentKey] ?? "");
      const total = groupTotals.get(groupKey) ?? 0;
      pctMap.set(
        cellKey(cell),
        cell.value != null && total > 0 ? cell.value / total : null,
      );
    }
  }

  return pctMap;
}

// Convert GridChartResult cells into the MinimalHeatmapSeries format that HeatmapTable consumes.
// Each cell becomes one series entry with groupings derived from the segment keys.
function gridCellsToHeatmapSeries(
  cells: GridChartResult["cells"],
  segmentKeys: string[],
  resolveLabel: LabelResolver,
) {
  return cells.map((cell) => {
    const grouping = segmentKeys.map((key) => {
      const rawValue = cell.segments[key]!;
      const label = resolveLabel(key as SendInsightDimension, rawValue);
      return {
        alias: DIMENSION_LABELS[key as SendInsightDimension] ?? key,
        value: label.name,
      };
    });

    // For single-segment data, add a synthetic second grouping so
    // generateHeatmapData uses case 2 (2D grid) and renders values
    // as rows (vertical list) instead of a single horizontal row.
    if (segmentKeys.length === 1) {
      grouping.push({ alias: "", value: "Value" });
    }

    return {
      metricName: "Value",
      grouping,
      data: [
        {
          metricValue: cell.value ?? 0,
          metadata: {
            numerator: cell.numerator ?? undefined,
            denominator: cell.denominator ?? undefined,
          },
        },
      ],
    };
  });
}

// Build a percentage lookup keyed by resolved label combinations so the
// cellValueFormatter can find the precomputed percentage for each HeatmapTable cell.
function buildResolvedPctMap(
  cells: GridChartResult["cells"],
  segmentKeys: string[],
  resolveLabel: LabelResolver,
  rawPctMap: Map<string, number | null>,
): Map<string, number | null> {
  const resolvedMap = new Map<string, number | null>();

  for (const cell of cells) {
    const rawKey = Object.entries(cell.segments)
      .map(([k, v]) => `${k}=${v}`)
      .join(":");

    const resolvedKey = segmentKeys
      .map((key) => {
        const rawValue = cell.segments[key]!;
        return resolveLabel(key as SendInsightDimension, rawValue).name;
      })
      .join(":");

    resolvedMap.set(resolvedKey, rawPctMap.get(rawKey) ?? null);
  }

  return resolvedMap;
}

function createTooltipRenderer(
  config: ChartConfig,
  displayFormat: DisplayFormat,
): TooltipRenderer {
  return (
    cell: HeatmapCellData,
    _formattedValue: string,
    _series: SeriesContext | undefined,
  ) => {
    const dimensions =
      cell.heatmapGrouping
        ?.map((g) => `${g.dimension}: ${g.value}`)
        .join(", ") ?? "";

    const parts: string[] = [];
    if (cell.numerator != null) {
      parts.push(
        `${metricLabel(config.numerator) ?? config.numerator}: ${cell.numerator.toLocaleString()}`,
      );
    }
    if (cell.denominator != null && config.denominator !== "none") {
      parts.push(
        `${metricLabel(config.denominator) ?? config.denominator}: ${cell.denominator.toLocaleString()}`,
      );
    }

    return (
      <Box py={2} px={2} minW="200px">
        <Column gap={2} align="flex-start">
          {dimensions && (
            <Text size="sm" color="white" fontWeight="medium">
              {dimensions}
            </Text>
          )}
          <Column align="flex-start">
            <Text size="sm" color="gray.400">
              Value
            </Text>
            <Box pl={2}>
              <Text
                size="sm"
                color="white"
                fontWeight="medium"
                variant="monospace"
              >
                {formatValue(cell.value, displayFormat)}
              </Text>
            </Box>
          </Column>
          {parts.length > 0 && (
            <Column align="flex-start">
              {parts.map((part) => (
                <Text key={part} size="sm" color="gray.400">
                  {part}
                </Text>
              ))}
            </Column>
          )}
        </Column>
      </Box>
    );
  };
}

function createCellValueFormatter(
  displayFormat: DisplayFormat,
  resolvedPctMap: Map<string, number | null> | null,
  nullValueKeys: Set<string>,
): CellValueFormatter {
  return (cell: HeatmapCellData, _defaultFormattedValue: string) => {
    const groupingKey =
      cell.heatmapGrouping?.map((g) => g.value).join(":") ?? "";

    if (nullValueKeys.has(groupingKey)) {
      return "–";
    }

    const formatted = formatValue(cell.value, displayFormat);

    if (resolvedPctMap) {
      const pct = resolvedPctMap.get(groupingKey);
      if (pct != null) {
        return `${formatPercent(pct)}\n(${formatted})`;
      }
    }

    return formatted;
  };
}

// Sort arrays use display labels because sorting runs after label resolution.
const TIME_BUCKET_DISPLAY_ORDER = TIME_BUCKET_ORDER.map(
  (key) => TIME_BUCKET_LABELS[key] ?? key,
);

const localeSortFn: SortFn = (a, b) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

function makeIndexSortFn(order: string[]): SortFn {
  return (a, b) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai === -1 && bi === -1) return localeSortFn(a, b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  };
}

const sortTimeBucket = makeIndexSortFn(TIME_BUCKET_DISPLAY_ORDER);
const sortDayOfWeek = makeIndexSortFn([...DAY_OF_WEEK_ORDER]);

function getSortFnForDimension(segmentKey: string): SortFn {
  if (segmentKey === "time_bucket") return sortTimeBucket;
  if (segmentKey === "day_of_week") return sortDayOfWeek;
  return localeSortFn;
}

function createRowSortFn(segmentKey: string): RowSortFn | undefined {
  if (segmentKey === "message_id") {
    return (a: HeatmapRow, b: HeatmapRow) =>
      a.dimension1.localeCompare(b.dimension1);
  }
  if (segmentKey === "channel") {
    return (a: HeatmapRow, b: HeatmapRow) => {
      const sumDenom = (row: HeatmapRow) =>
        row.cells.reduce((sum, c) => sum + (c.denominator ?? 0), 0);
      return sumDenom(b) - sumDenom(a);
    };
  }
  return undefined;
}

type SortMode = "default" | "value_desc" | "value_asc";

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "value_desc", label: "Value: high to low" },
  { value: "value_asc", label: "Value: low to high" },
];

function sumRowValues(row: HeatmapRow): number {
  return row.cells.reduce((sum, c) => sum + (c.value ?? 0), 0);
}

interface SendInsightsGridChartProps {
  config: ChartConfig;
  result: GridChartResult;
  resolveLabel: LabelResolver;
}

const CHANNEL_FILTER_ALL = "all";

export function SendInsightsGridChart({
  config,
  result,
  resolveLabel,
}: SendInsightsGridChartProps) {
  const displayFormat = getDisplayFormat(config.denominator);
  const [filterChannelType, setFilterChannelType] =
    useState<string>(CHANNEL_FILTER_ALL);
  const [sortMode, setSortMode] = useState<SortMode>("default");
  const rawCells = result.cells;

  if (rawCells.length === 0) {
    return (
      <Box p={4}>
        <Text color="text.secondary">No data available for this chart.</Text>
      </Box>
    );
  }

  const segmentKeys = Object.keys(rawCells[0]!.segments);
  const rowSegmentKey = segmentKeys[0]!;
  const channelFilterApplicable =
    rowSegmentKey === "message_id" || rowSegmentKey === "channel";

  const availableChannelTypes: string[] = [];
  if (channelFilterApplicable) {
    const seen = new Set<string>();
    for (const cell of rawCells) {
      const rawValue = cell.segments[rowSegmentKey];
      if (rawValue == null) continue;
      const label = resolveLabel(
        rowSegmentKey as SendInsightDimension,
        rawValue,
      );
      if (label.channelType && !seen.has(label.channelType)) {
        seen.add(label.channelType);
        availableChannelTypes.push(label.channelType);
      }
    }
  }
  const showChannelFilter = availableChannelTypes.length >= 2;
  const showSort = rowSegmentKey === "message_id";
  const showControls = showChannelFilter || showSort;
  const filterIsActive =
    channelFilterApplicable && filterChannelType !== CHANNEL_FILTER_ALL;

  const cells = filterIsActive
    ? rawCells.filter((cell) => {
        const rawValue = cell.segments[rowSegmentKey];
        if (rawValue == null) return false;
        const label = resolveLabel(
          rowSegmentKey as SendInsightDimension,
          rawValue,
        );
        return label.channelType === filterChannelType;
      })
    : rawCells;

  const channelTypeOptions = [
    { value: CHANNEL_FILTER_ALL, label: "All" },
    ...availableChannelTypes.map((ct) => ({
      value: ct,
      label: ChannelOptionsMap[ct]?.label ?? ct,
    })),
  ];

  const totalRowCount = new Set(
    rawCells
      .map((c) => c.segments[rowSegmentKey])
      .filter((v): v is string | number => v != null),
  ).size;
  const filteredRowCount = new Set(
    cells
      .map((c) => c.segments[rowSegmentKey])
      .filter((v): v is string | number => v != null),
  ).size;

  const controls = showControls ? (
    <Row gap={3} align="center" px={2} pb={2} flexWrap="wrap">
      {showChannelFilter && (
        <Select
          width="sm"
          options={channelTypeOptions}
          value={filterChannelType}
          onChange={(val) => {
            if (val) setFilterChannelType(val);
          }}
        />
      )}
      {showSort && (
        <Select
          width="sm"
          options={SORT_OPTIONS}
          value={sortMode}
          onChange={(val) => {
            if (val) setSortMode(val);
          }}
        />
      )}
      {filterIsActive && (
        <Text size="sm" color="text.secondary">
          {filteredRowCount} of {totalRowCount} rows
        </Text>
      )}
    </Row>
  ) : null;

  if (cells.length === 0) {
    return (
      <Column gap={0}>
        {controls}
        <Box p={4}>
          <Text color="text.secondary">
            No data matches the selected filters.
          </Text>
        </Box>
      </Column>
    );
  }

  const series = gridCellsToHeatmapSeries(cells, segmentKeys, resolveLabel);

  const rowLabelByName = new Map<string, SendInsightLabel>();
  for (const cell of cells) {
    const rawValue = cell.segments[rowSegmentKey];
    if (rawValue == null) continue;
    const label = resolveLabel(rowSegmentKey as SendInsightDimension, rawValue);
    rowLabelByName.set(label.name, label);
  }

  const rowLabelRenderer = (row: HeatmapRow) => {
    const label = rowLabelByName.get(row.dimension1);
    const name = label?.name ?? row.dimension1 ?? "--";
    return (
      <Row align="center" gap={2} minWidth={0} width="100%">
        {label?.channelType && (
          <Box flexShrink={0}>
            <ChannelIcon type={label.channelType} size="sm" />
          </Box>
        )}
        <Tooltip message={name}>
          <Box
            flex={1}
            minWidth={0}
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {label?.url ? (
              <Link href={label.url} target="_blank" rel="noopener noreferrer">
                {name}
              </Link>
            ) : (
              <Text size="sm" variant="monospace" color="text.secondary">
                {name}
              </Text>
            )}
          </Box>
        </Tooltip>
      </Row>
    );
  };

  const hasRichRowLabels = Array.from(rowLabelByName.values()).some(
    (l) => l.channelType || l.url,
  );

  const hasCampaignIds = Array.from(rowLabelByName.values()).some(
    (l) => l.campaignIds?.length,
  );

  const auxColumnRenderer = hasCampaignIds
    ? (row: HeatmapRow) => {
        const label = rowLabelByName.get(row.dimension1);
        const text = label?.campaignIds?.length
          ? label.campaignIds.join(", ")
          : null;
        if (!text) {
          return (
            <Text size="sm" color="text.tertiary">
              —
            </Text>
          );
        }
        return (
          <TextWithTooltip
            message={text}
            size="sm"
            variant="monospace"
            color="text.tertiary"
            isTruncated
          >
            {text}
          </TextWithTooltip>
        );
      }
    : undefined;

  const percentMode = getPercentMode(config);
  const rawPctMap = percentMode
    ? computeCellPercentages(cells, percentMode)
    : null;
  const resolvedPctMap = rawPctMap
    ? buildResolvedPctMap(cells, segmentKeys, resolveLabel, rawPctMap)
    : null;

  // Track which cells had null values (stored as 0 in series data)
  const nullValueKeys = new Set<string>();
  for (const cell of cells) {
    if (cell.value == null) {
      const key = segmentKeys
        .map(
          (k) =>
            resolveLabel(k as SendInsightDimension, cell.segments[k]!).name,
        )
        .join(":");
      nullValueKeys.add(key);
    }
  }

  const tooltipRenderer = createTooltipRenderer(config, displayFormat);
  const cellValueFormatter = createCellValueFormatter(
    displayFormat,
    resolvedPctMap,
    nullValueKeys,
  );
  const rowSortFn: RowSortFn | undefined =
    sortMode === "value_desc"
      ? (a, b) => sumRowValues(b) - sumRowValues(a)
      : sortMode === "value_asc"
        ? (a, b) => sumRowValues(a) - sumRowValues(b)
        : createRowSortFn(rowSegmentKey);
  const groupBySortFns: [SortFn, SortFn] = [
    getSortFnForDimension(segmentKeys[0] ?? ""),
    getSortFnForDimension(segmentKeys[1] ?? ""),
  ];

  return (
    <Column gap={0} whiteSpace="pre-line">
      {controls}
      <HeatmapTable
        data={series}
        scale={
          displayFormat === "percentage"
            ? GraphScale.Percent
            : GraphScale.Linear
        }
        isLoading={false}
        showActionsAndLegend={false}
        isTruncated={false}
        tooltipRenderer={tooltipRenderer}
        cellValueFormatter={cellValueFormatter}
        rowSortFn={rowSortFn}
        groupBySortFns={groupBySortFns}
        rowLabelRenderer={hasRichRowLabels ? rowLabelRenderer : undefined}
        rowLabelWidth={hasRichRowLabels ? "minmax(180px, 280px)" : undefined}
        auxColumnHeader={auxColumnRenderer ? "Campaign IDs" : undefined}
        auxColumnRenderer={auxColumnRenderer}
        auxColumnWidth={auxColumnRenderer ? "minmax(80px, 140px)" : undefined}
      />
    </Column>
  );
}
